<script>
    import dressing from "@intellectability/platform-common";
    import {
        EntityChangeHistory,
        Icon,
        LazyLoader,
        MegaPopup,
        Placeholder,
        toast
    } from "@intellectability/platform-common/sv";
    import {messages, urlParams, userSecurity} from "@intellectability/platform-common/sv/state";
    import {actionCheck} from "@intellectability/platform-common/utils";
    import {Breadcrumb, BreadcrumbItem, Col, Row} from "@sveltestrap/sveltestrap";
    import {onMount} from "svelte";
    import {on} from "svelte/events";
    import {MediaQuery} from "svelte/reactivity";

    import {ACTIONS} from "@intellectability/platform-common/constants";
    import ManageAvatar from "./ManageAvatar.svelte";
    import {getProfileTabs} from "./Tabs.svelte.js";

    import "https://early.webawesome.com/webawesome@3.0.0-alpha.13/dist/components/avatar/avatar.js";
    import "https://early.webawesome.com/webawesome@3.0.0-alpha.13/dist/components/card/card.js";

    import {fade} from "svelte/transition";

    const {
        USER_TAB_DETAILS_VIEW, USER_TAB_NOTIFICATIONS_VIEW, USER_TAB_TRAININGS_VIEW, USER_TAB_ROLES_VIEW,
        USER_ONE_PAGE_DESCRIPTION_VIEW,

        USER_PHOTO_VIEW,
        USER_BREADCRUMB_VIEW,
        USER_SISTER_SITE_LINK
    } = ACTIONS;

    let hasAccess = $derived(actionCheck(userSecurity.userActions));

    let {id: profileID = null} = $props();

    let loading = $derived(userSecurity.loggedIn !== true);

    /**
     * @typedef UserStatus
     * @property {number} id - the internal status id
     * @property {string} status - The name of the status
     * @property {"Y"|"N"} default - there should be one status set to "Y". This indicates the "Active" status.
     */

    /**
     * @type {UserStatus[]}
     */
    let userStatuses = $state.raw([]);
    let activeStatusID = $derived(userStatuses.find(status => status.default === "Y")?.id || null);

    let profile = $state({
        cell_carrier: "",
        credentials: "",
        email: "",
        employer: "",
        employer_id: null,
        first_name: "",
        hire_date: "",
        id: null,
        is_master: false,
        last_name: "",
        licence_number: "",
        master_username: "",
        middle_name: "",
        photo: "",
        region: null,
        roles: [],
        scope: null,
        signature: "",
        status: null,
        supervisor: null,
        supervisor_name: "",
        title: "",
        username: ""
    });

    // security for user profile
    let isExistingProfile = $derived(Number.isInteger(profileID));
    let isActiveProfile = $derived(profile.status === activeStatusID);
    let loggedInUser = $derived(userSecurity.loggedInUser);
    let loggedInUserID = $derived(Number(loggedInUser.id) || "");
    let ownProfile = $derived(loggedInUserID === profileID);

    let business_rules = {
        IMPLEMENT_LINKED_ACCOUNTS: "implement_linked_accounts"
    };

    let allowLinkAccount = $derived(userSecurity.businessRules.includes(business_rules.IMPLEMENT_LINKED_ACCOUNTS));

    let tabGroupRef = $state(null);

    let tabProps = $derived({
        id: profileID,
        entityType: "user",
        isActiveProfile
    });

    let showChangeLog = $state(false);

    $effect(function () {
        if (tabGroupRef) {
            // listen for wa-tab-show events which indicates a tab change and update the url params
            on(tabGroupRef, "wa-tab-show", function (e) {
                let tab_name = e.detail.name || "details";
                urlParams.set("tab", tab_name);
            });
        }
    });

    onMount(function load() {

        if (profileID) {
            // get details for this profile
            loadProfile();
        }

        dressing.get("/users/statuses").then(({data: res}) => {
            const {success, data} = res;
            if (success === true) {
                userStatuses = data.users_statuses ?? [];
            }
            return success;
        });
    });

    function loadProfile() {
        dressing.get(`/users/full/${profileID}`)
        .then(({data: res}) => {

            const {success, data} = res;
            if (success === true) {
                const user = data;

                let user_roles = [];
                if (Array.isArray(user.roles) && user.roles.length > 0) {
                    // just get the ids
                    user_roles = user.roles.map(role => role.role_id);
                }

                profile = {
                    calendar: user.calendar || "",
                    cell_carrier: user.cell_carrier || "",
                    credentials: user.credentials || "",
                    email: user.email || "",
                    employer: user.employer || "",
                    employer_id: Number(user.employer_id) || null,
                    first_name: user.first_name || "",
                    hire_date: user.hire_date || "",
                    id: Number(user.id) || null,
                    is_master: user.is_master,
                    last_name: user.last_name || "",
                    licence_number: user.licence_number || "",
                    master_username: user.master_username || "",
                    middle_name: user.middle_name || "",
                    photo: user.photo || "",
                    region: Number(user.region) || null,
                    roles: user_roles,
                    scope: Number(user.scope) || null,
                    signature: user.signature || "",
                    status: Number(user.status) || null,
                    supervisor: Number(user.supervisor) || null,
                    supervisor_name: user.supervisor_name || "",
                    title: user.title || "",
                    username: user.username || ""
                };

                document.title = (`User Details for ${profile.first_name} ${profile.last_name}`);
            }
            else {
                toast.error("Error loading user details");
            }
            return success;
        }).catch(() => {
            toast.error("Error retrieving user details");
        });
    }

    // tabs location
    const large = new MediaQuery("(min-width: 992px)");

    // new users can only see details tab
    let currentTab = $derived((isExistingProfile && urlParams.get("tab")) || "details");

    let userTabs = $derived.by(() => getProfileTabs(ownProfile));

</script>

<!-- Breadcrumbs -->
{#if hasAccess(USER_BREADCRUMB_VIEW)}
    <Breadcrumb divider={" "} class="breadcrumbs mb-3">
        <BreadcrumbItem>
            <a href="/index.php?action=30000">
                <Icon icon="user" class="me-1"/>
                users
            </a>
        </BreadcrumbItem>
        <BreadcrumbItem class="mx-3">
            <Icon icon="chevron-right"/>
        </BreadcrumbItem>
        <BreadcrumbItem>
            {profileID ? "user details for " + (profile.first_name + " " + profile.last_name) : "add new user"}
        </BreadcrumbItem>
    </Breadcrumb>
{/if}
<!-- Page Title -->
<h1 class="text-primary-700">
    {#if loading}
        <Placeholder/>
    {:else}
        {#if isExistingProfile}
            <!--{"User Details for "}<span class="text-primary-500">{profile.first_name + " " + profile.last_name}</span>-->
        {:else}
            Add New User
        {/if}
    {/if}
</h1>
{#if isExistingProfile && isActiveProfile}
    <div transition:fade>
        <wa-callout appearance="filled" class="mb-4">
            <Row class="h-100 gx-3">
                <Col md="auto" class="align-self-center gx-5 d-flex">
                    <ManageAvatar
                            allowUpdate={ownProfile}
                            {profileID}
                            initials={profile.first_name.slice(0, 1) + profile.last_name.slice(0, 1)}
                            bind:imageURLStub={profile.photo}
                            onupdate={loadProfile}
                    />
                </Col>
                <Col class="d-flex flex-column justify-content-center">
                    <wa-card appearance="plain" style="--inner-border-color: var(--hrst-secondary-400);">
                        <div class="text-center text-md-start">
                            <div>
                                <div class="d-inline-flex align-items-center mb-1">
                                    <h3 class="mb-0 me-2">{[profile.first_name, profile.last_name].join(" ")}</h3>
                                    <div>
                                        <wa-badge pill>{userStatuses.find(
                                            s => profile.status === s.id)?.status}</wa-badge>
                                    </div>
                                </div>
                            </div>
                            <div class="form-text mb-3">{profile.title}</div>
                            <div id="role-icons">
                                <wa-avatar id="role-rater">
                                    <wa-icon
                                            slot="icon" family="duotone"
                                            name="ballot-check" variant="solid"
                                    ></wa-icon>
                                </wa-avatar>
                                <wa-tooltip
                                        for="role-rater"
                                        placement="bottom"
                                >
                                    User has Rater role
                                </wa-tooltip>
                                <wa-avatar id="role-clinical-reviewer">
                                    <wa-icon
                                            slot="icon" family="duotone"
                                            name="user-doctor" variant="solid"
                                    ></wa-icon>
                                </wa-avatar>
                                <wa-tooltip
                                        for="role-clinical-reviewer"
                                        placement="bottom"
                                >
                                    User has Clinical Reviewer role
                                </wa-tooltip>
                            </div>
                        </div>
                        {#if !ownProfile}
                            <div slot="footer">
                                <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
                                <wa-button
                                        appearance="outlined"
                                        class="w-100"
                                        onclick={() => messages.open({
                                            recipient: profileID,
                                            onclose: (sent) => sent && toast.success("Message sent")
                                        })
                                    }>
                                    <wa-icon slot="prefix" name="at"></wa-icon>
                                    Contact
                                </wa-button>
                            </div>
                        {/if}
                    </wa-card>
                </Col>
                <Col xs="auto" class="d-none d-md-block">
                    <div class="d-grid gap-2 mt-3 me-2">
                        <div class="">
                            <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
                            <wa-button id="incomplete-training-indicator" appearance="outlined" variant="danger"
                                       onclick={() => currentTab = "trainings"}>
                                <wa-icon name="graduation-cap" family="duotone" variant="regular"></wa-icon>
                                <wa-badge variant="danger" pill>3</wa-badge>
                            </wa-button>
                            <wa-tooltip showDelay={350}
                                        for="incomplete-training-indicator"
                                        placement="left-start"
                            >
                                Alexander has 3 incomplete trainings, press to view
                            </wa-tooltip>
                        </div>
                        <div>
                            <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
                            <wa-button id="change-log-history" appearance="outlined" variant="brand"
                                       onclick={() => showChangeLog = !showChangeLog}>
                                <wa-icon name="clock-rotate-left" family="duotone" variant="solid"></wa-icon>
                            </wa-button>
                            <wa-tooltip showDelay={350}
                                        for="change-log-history"
                                        placement="left-start"
                            >
                                View user change history
                            </wa-tooltip>

                            <MegaPopup
                                    bind:active={showChangeLog}
                                    for="change-log-history">
                                <div class="py-2 px-3 rounded-1 bg-white">
                                    {@render changeHistory()}
                                </div>
                            </MegaPopup>
                        </div>
                    </div>
                </Col>
            </Row>
        </wa-callout>
    </div>
{/if}

<div class="tab-container">
    <!-- Show tabs existing profile, otherwise show only the details page -->
    {#if isExistingProfile}
        {#key userTabs}
            <wa-tab-group
                    bind:this={tabGroupRef}
                    placement={large.current ? "top" : "top"}
                    active={currentTab}
            >
                {#each userTabs as tab}
                    {@const tabIconClass = currentTab === tab.key ? "text-primary-500" : "text-secondary-600"}
                    <wa-tab panel={tab.key}>
                        {#if tab.icon}
                            <Icon icon={tab.icon} class={`${tabIconClass} me-2 ` + tab.iconClass}/>
                        {/if}
                        {tab.title}
                    </wa-tab>
                {/each}

                {#each userTabs as tab}
                    <wa-tab-panel name={tab.key}>
                        {#key currentTab}
                            <div in:fade={{duration: 550}}>
                                {#if tab.lazyLoad}
                                    <LazyLoader>
                                        <tab.component bind:profile={profile} {...tab.props} {...tabProps}/>
                                    </LazyLoader>
                                {:else}
                                    <tab.component bind:profile={profile} {...tab.props} {...tabProps}/>
                                {/if}
                            </div>
                        {/key}
                    </wa-tab-panel>
                {/each}
            </wa-tab-group>
        {/key}
    {:else}
        {@const tab = userTabs.find(tab => tab.key === "details")}
        {#if tab}
            <tab.component id={profileID} entityType="user" bind:profile={profile} {...tab.props}/>
        {/if}
    {/if}
</div>

{#snippet changeHistory()}
    <EntityChangeHistory entityType={"user"} entityID={profileID}></EntityChangeHistory>
{/snippet}

<style>

    .tab-container {
        /* Keeps the page from jumping when switching between tabs */
        min-height: 2000px;
    }


    div#role-icons {
        wa-avatar {
            --size: 3rem;
        }
    }

    wa-tab-group {
        --indicator-color: var(--hrst-primary-500);
        --track-color: var(--hrst-secondary-100);
        --track-width: 3px;
    }

    wa-tab {
        --active-tab-color: var(--hrst-primary-500);
        font-family: "Arimo", sans-serif;
    }
</style>